import React, { useEffect, useRef, useState } from 'react';
import { Box, Grid, Typography, Card, IconButton, Modal, ModalClose, Sheet } from '@mui/joy';
import { ArrowsOut as Maximize2 } from '@phosphor-icons/react';
import { GoogleStreetView } from './GoogleStreetView';

interface CardinalViewsPanelProps {
  location: { lat: number; lng: number };
}

const CARDINAL_VIEWS = [
  { label: 'North', heading: 0 },
  { label: 'East', heading: 90 },
  { label: 'South', heading: 180 },
  { label: 'West', heading: 270 },
] as const;

const TILE_HEIGHT = 250;

// Camera framing for the oblique 3D views: tilt is measured from straight-down
// (0 = overhead), range is the camera-to-property distance in meters.
const OBLIQUE_TILT_DEG = 55;
const OBLIQUE_RANGE_M = 120;

// Maps JS v3.65 (May 2026) removed 45° imagery from raster satellite maps —
// tilt/heading are now silently ignored there, so oblique views require the
// photorealistic 3D element (maps3d library), which is served by the Map Tiles
// API. Probe that API once per page load; when it is disabled for the key we
// fall back to plain top-down satellite tiles instead of a black 3D map.
let obliqueSupportPromise: Promise<boolean> | null = null;
function checkObliqueSupport(): Promise<boolean> {
  if (!obliqueSupportPromise) {
    obliqueSupportPromise = (async () => {
      try {
        const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
        if (!apiKey || typeof window.google?.maps?.importLibrary !== 'function') return false;
        const res = await fetch(`https://tile.googleapis.com/v1/3dtiles/root.json?key=${apiKey}`);
        if (!res.ok) return false;
        await google.maps.importLibrary('maps3d');
        return true;
      } catch {
        return false;
      }
    })();
  }
  return obliqueSupportPromise;
}

// The 3D camera works in absolute altitudes, so center the view on the ground
// elevation at the property — with altitude 0 a close-in camera can end up
// under the terrain. Falls back to 0 (sea level) if the lookup fails.
async function getGroundAltitude(location: { lat: number; lng: number }): Promise<number> {
  try {
    const service = new google.maps.ElevationService();
    const { results } = await service.getElevationForLocations({ locations: [location] });
    return results?.[0]?.elevation ?? 0;
  } catch {
    return 0;
  }
}

// Shared label style
const labelStyle = {
  position: 'absolute',
  top: 6,
  left: 6,
  zIndex: 1,
  backgroundColor: 'rgba(0,0,0,0.7)',
  color: '#fff',
  px: 1,
  py: 0.25,
  borderRadius: 'xs',
  fontWeight: 'bold',
  fontSize: '10px',
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
} as const;

// Shared fullscreen button style
const fullscreenBtnStyle = {
  position: 'absolute',
  top: 4,
  right: 4,
  zIndex: 1,
  minWidth: 28,
  minHeight: 28,
  backgroundColor: 'rgba(0,0,0,0.6)',
  color: '#fff',
  '&:hover': { backgroundColor: 'rgba(0,0,0,0.85)' },
} as const;

// Oblique photorealistic 3D view of the property from one cardinal direction
function Oblique3DView({
  location,
  altitude,
  heading,
  interactive,
}: {
  location: { lat: number; lng: number };
  altitude: number;
  heading: number;
  interactive: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let cancelled = false;

    (async () => {
      const { Map3DElement } = await google.maps.importLibrary('maps3d');
      if (cancelled) return;
      const map = new Map3DElement({
        center: { lat: location.lat, lng: location.lng, altitude },
        range: OBLIQUE_RANGE_M,
        tilt: OBLIQUE_TILT_DEG,
        heading,
        mode: 'SATELLITE',
        defaultUIHidden: !interactive,
        gestureHandling: interactive ? 'GREEDY' : 'AUTO',
      });
      map.style.width = '100%';
      map.style.height = '100%';
      container.replaceChildren(map);
    })();

    return () => {
      cancelled = true;
      container.replaceChildren();
    };
  }, [location, altitude, heading, interactive]);

  return (
    <Box
      ref={containerRef}
      sx={{
        height: '100%',
        width: '100%',
        borderRadius: 'sm',
        overflow: 'hidden',
        ...(interactive ? {} : { pointerEvents: 'none' }),
      }}
    />
  );
}

// Top-down raster satellite fallback, used when the Map Tiles API is
// unavailable (heading/tilt have no effect on raster maps since v3.65)
function FlatSatelliteView({
  location,
  interactive,
}: {
  location: { lat: number; lng: number };
  interactive: boolean;
}) {
  const mapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!mapRef.current || !window.google?.maps) return;

    new google.maps.Map(mapRef.current, {
      center: location,
      zoom: 20,
      mapTypeId: google.maps.MapTypeId.SATELLITE,
      disableDefaultUI: !interactive,
      zoomControl: interactive,
      streetViewControl: false,
      fullscreenControl: false,
      mapTypeControl: false,
      rotateControl: false,
      gestureHandling: interactive ? 'greedy' : 'none',
      keyboardShortcuts: interactive,
    });
  }, [location, interactive]);

  return (
    <Box
      ref={mapRef}
      sx={{ height: '100%', width: '100%', borderRadius: 'sm', overflow: 'hidden' }}
    />
  );
}

// Label + expand-button chrome shared by the grid tiles
function TileFrame({
  label,
  onExpand,
  children,
}: {
  label: string;
  onExpand: () => void;
  children: React.ReactNode;
}) {
  return (
    <Box sx={{ position: 'relative', height: TILE_HEIGHT, width: '100%' }}>
      {children}
      <Typography level="body-xs" sx={labelStyle}>
        {label}
      </Typography>
      <IconButton size="sm" variant="plain" sx={fullscreenBtnStyle} onClick={onExpand}>
        <Maximize2 size={14} />
      </IconButton>
    </Box>
  );
}

export const CardinalViewsPanel: React.FC<CardinalViewsPanelProps> = ({ location }) => {
  const [expandedView, setExpandedView] = useState<{
    type: 'cardinal' | 'street';
    label: string;
    heading: number;
  } | null>(null);
  // 'loading' until the 3D-tiles probe resolves, then '3d' or 'flat'
  const [viewMode, setViewMode] = useState<'loading' | '3d' | 'flat'>('loading');
  const [groundAltitude, setGroundAltitude] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const supported = await checkObliqueSupport();
      if (cancelled) return;
      if (!supported) {
        setViewMode('flat');
        return;
      }
      const altitude = await getGroundAltitude(location);
      if (cancelled) return;
      setGroundAltitude(altitude);
      setViewMode('3d');
    })();
    return () => {
      cancelled = true;
    };
  }, [location]);

  const renderCardinalView = (heading: number, interactive: boolean) => {
    if (viewMode === '3d') {
      return (
        <Oblique3DView
          location={location}
          altitude={groundAltitude}
          heading={heading}
          interactive={interactive}
        />
      );
    }
    if (viewMode === 'flat') {
      return <FlatSatelliteView location={location} interactive={interactive} />;
    }
    return <Box sx={{ height: '100%', borderRadius: 'sm', bgcolor: 'background.level2' }} />;
  };

  return (
    <>
      <Card variant="outlined" sx={{ mt: 2 }}>
        <Typography level="title-sm" sx={{ mb: 1, fontWeight: 'bold' }}>
          Property Views
        </Typography>
        <Grid container spacing={1}>
          {CARDINAL_VIEWS.map((view) => (
            <Grid key={view.label} xs={6}>
              <TileFrame
                label={view.label}
                onExpand={() =>
                  setExpandedView({ type: 'cardinal', label: view.label, heading: view.heading })
                }
              >
                {renderCardinalView(view.heading, false)}
              </TileFrame>
            </Grid>
          ))}
          <Grid xs={6}>
            <Box sx={{ position: 'relative' }}>
              <GoogleStreetView
                location={location}
                height={TILE_HEIGHT}
                pitch={10}
                zoom={1}
              />
              <Typography level="body-xs" sx={labelStyle}>
                Street View
              </Typography>
              <IconButton
                size="sm"
                variant="plain"
                sx={fullscreenBtnStyle}
                onClick={() =>
                  setExpandedView({ type: 'street', label: 'Street View', heading: 0 })
                }
              >
                <Maximize2 size={14} />
              </IconButton>
            </Box>
          </Grid>
        </Grid>
      </Card>

      {/* Fullscreen Modal */}
      <Modal
        open={!!expandedView}
        onClose={() => setExpandedView(null)}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Sheet
          variant="outlined"
          sx={{
            width: '90vw',
            height: '85vh',
            borderRadius: 'md',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <ModalClose sx={{ zIndex: 2 }} />
          <Typography
            level="title-md"
            sx={{
              position: 'absolute',
              top: 12,
              left: 16,
              zIndex: 1,
              backgroundColor: 'rgba(0,0,0,0.7)',
              color: '#fff',
              px: 1.5,
              py: 0.5,
              borderRadius: 'sm',
              fontWeight: 'bold',
            }}
          >
            {expandedView?.label}
          </Typography>
          <Box sx={{ width: '100%', height: '100%' }}>
            {expandedView?.type === 'cardinal' ? (
              renderCardinalView(expandedView.heading, true)
            ) : expandedView?.type === 'street' ? (
              <GoogleStreetView
                location={location}
                height="100%"
                pitch={10}
                zoom={1}
              />
            ) : null}
          </Box>
        </Sheet>
      </Modal>
    </>
  );
};
